/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from "react";
import PropTypes from "prop-types";
import Head from "next/head";
import ListingBadge from "components/ListingBadge";
import { Price } from "components/Domain/Pricing";
import { formatNumber } from "util/strings";
import styles from "./style.module.scss";

export const Skeleton = () => (
  <li className={`${styles.card} ${styles.skeleton}`} />
);

const generateSubtitle = (building) => {
  const neighborhoodName = building?.neighborhood?.name;
  const marketName = building?.market?.name;
  const submarketName = building?.submarket?.name;

  if (!marketName) return "";

  if (neighborhoodName && submarketName) {
    return `${submarketName} - ${neighborhoodName}`;
  }
  if (submarketName && !neighborhoodName) {
    return submarketName;
  }
  if (neighborhoodName && !submarketName) {
    return neighborhoodName;
  }
  if (!submarketName && !neighborhoodName) {
    return marketName;
  }
  // Linting rules require a return at the end of an arrow function
  return "";
};

function ListingCard(
  {
    listing,
    listing: {
      building: { hasVirtualTours },
    },
    // These default values need to be supplied until converted to TS otherwise consumption in <PublicSearch /> breaks
    className,
    href = null,
    eager = false,
    onClick = () => {},
    onMouseEnter = () => {},
    onMouseLeave = () => {},
  },
  ref
) {
  const inlineStyles = {
    position: "absolute",
    top: "20px",
    left: "20px",
  };

  const occupancy = listing.maxOccupancy
    ? formatNumber(listing.maxOccupancy)
    : `~${formatNumber(listing.estimatedMaxOccupancy)}`;
  const size = listing.squareFeet
    ? formatNumber(listing.squareFeet)
    : `~${formatNumber(listing.estimatedSquareFeet)}`;

  // We use high 'importance' priority hints and a preload link for above the
  // fold images (those explicity marked "eager=eager") to improve
  // lighthouse scores.
  return (
    <li
      className={`${styles.card} ${className}`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <a href={href} ref={ref}>
        <div className={styles.listingCardImageWrapper}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt="listing"
            className={styles.listingCardImage}
            src={listing.coverImage.url}
          />
          {eager && (
            <Head>
              <link
                rel="preload"
                href={listing.coverImage.url}
                as="image"
                importance="high"
              />
            </Head>
          )}
          {listing.brokerInsights &&
            listing.brokerInsights.length > 0 &&
            !hasVirtualTours && (
              <ListingBadge type="brokerInsight" style={inlineStyles} />
            )}
          {hasVirtualTours && (
            <ListingBadge type="virtualTour" style={inlineStyles} />
          )}
        </div>
        <div className={styles.inner}>
          <p className={styles.subtitle}>
            {generateSubtitle(listing.building)}
          </p>
          <p className={`${styles.title}`}>
            {listing.building.address} - {listing.floorAndSuite}
          </p>
          <ul className={styles.details}>
            <li>
              {size} <span>sqft</span>
            </li>
            <li>
              <Price
                leaseType={listing.leaseType}
                status={listing.status}
                displayPSF={listing.displayPSF}
                nhoodPsf={listing.building.neighborhood?.yearlyPricePerFoot}
              />
            </li>
            <li>
              {occupancy} <span>seats</span>
            </li>
          </ul>
        </div>
      </a>
    </li>
  );
}

// This is only needed if we want to access things like innerRef. We don't do this but console complains about it and it was mentioned in the PR so I'm adding it here. AFAIK adding compatiblity could be a fix from next in the future.
export default React.forwardRef(ListingCard);

ListingCard.propTypes = {
  className: PropTypes.string,
  eager: PropTypes.bool,
  listing: PropTypes.shape({
    coverImage: PropTypes.shape({
      url: PropTypes.string,
    }),
    building: PropTypes.shape({
      address: PropTypes.string.isRequired,
      hasVirtualTours: PropTypes.bool,
      neighborhood: PropTypes.shape({
        name: PropTypes.string.isRequired,
        yearlyPricePerFoot: PropTypes.number,
      }),
      market: PropTypes.shape({
        name: PropTypes.string,
      }),
      submarket: PropTypes.shape({
        name: PropTypes.string,
      }),
    }).isRequired,
    floorAndSuite: PropTypes.string.isRequired,
    squareFeet: PropTypes.number.isRequired,
    accurateMonthlyPrice: PropTypes.number,
    displayPSF: PropTypes.string,
    leaseType: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    maxOccupancy: PropTypes.number,
    estimatedMaxOccupancy: PropTypes.number,
    estimatedSquareFeet: PropTypes.number,
    brokerInsights: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      })
    ),
  }).isRequired,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  href: PropTypes.string,
};

ListingCard.defaultProps = {
  eager: false,
  onClick: () => {},
  className: null,
  onMouseEnter: () => {},
  onMouseLeave: () => {},
  href: null,
};
